import React from 'react';
import PropTypes from 'prop-types';
import { Popper } from 'react-popper';
import Portal from '@ids/portal';

const { bool, element, object, number, string } = PropTypes;

export const placements = {
  TOP: 'top-center',
  TOP_LEFT: 'top-left',
  TOP_RIGHT: 'top-right',
  BOTTOM: 'bottom-center',
  BOTTOM_LEFT: 'bottom-left',
  BOTTOM_RIGHT: 'bottom-right',
  LEFT: 'left-middle',
  LEFT_TOP: 'left-top',
  LEFT_BOTTOM: 'left-bottom',
  RIGHT: 'right-middle',
  RIGHT_TOP: 'right-top',
  RIGHT_BOTTOM: 'right-bottom'
};

const placementMap = {
  top: placements.TOP,
  'top-start': placements.TOP_LEFT,
  'top-end': placements.TOP_RIGHT,
  bottom: placements.BOTTOM,
  'bottom-start': placements.BOTTOM_LEFT,
  'bottom-end': placements.BOTTOM_RIGHT,
  left: placements.LEFT,
  'left-start': placements.LEFT_TOP,
  'left-end': placements.LEFT_BOTTOM,
  right: placements.RIGHT,
  'right-start': placements.RIGHT_TOP,
  'right-end': placements.RIGHT_BOTTOM
};

/** Translate  position and alignment props to Popper placement */
const getPlacementKey = (alignment, position) => {
  let placement;

  switch (alignment) {
    case 'left':
    case 'top':
      placement = `${position}-start`;
      break;
    case 'right':
    case 'bottom':
      placement = `${position}-end`;
      break;
    default:
      placement = position;
      break;
  }

  return placement;
};

/** given alignment and position, returns the proper placement */
export const getPlacement = (alignment, position) =>
  placementMap[getPlacementKey(alignment, position)];

/** given proper placementKey, returns a class name with position and alignment  */
const getPlacementClass = (componentName, placementKey) => {
  const [position, alignment] = (placementMap[placementKey] || '').split('-');
  if (!componentName || !position || !alignment) {
    return '';
  }

  return `${componentName}-${position}Position-${alignment}Alignment`;
};

/** Translate offsetX and offsetY props to Popper modifiers object */
const getOffset = (offsetX, offsetY) => {
  if (!offsetX && !offsetY) {
    return {};
  }

  return {
    setOffset: {
      enabled: true,
      fn: data => {
        // eslint-disable-next-line no-param-reassign
        data.styles = {
          ...data.styles,
          left: `${offsetX}px`,
          top: `${-offsetY}px`
        };
        return data;
      }
    }
  };
};

/** Position an element on the page */
export default class Position extends React.PureComponent {
  static propTypes = {
    /**
     * alignment: a string variable
     * vertical alignments: top, bottom, middle
     * horizontal alignments: left, right, center
     * NOTE: horizontal alignments cannot be paired with horizontal positions
     * */
    alignment: string,
    /**
     * The element would be rendered in correct position to show.
     */
    children: element.isRequired,
    /**
     * componentName is required as prefix with rendered position to generate a position class name,
     * if componentName is not given, then no position class name been added.
     * Position class name would like: componentName-topPosition-leftAlignment
     */
    componentName: string,
    /**
     * offsetX: the x axis pixel distance that the popup
     * should be offset from targetElement
     * */
    offsetX: number,
    /**
     * offsetY: the y axis pixel distance that the popup
     * should be offset from targetElement
     * */
    offsetY: number,
    /**
     * position: a string variable
     * vertical positions: top, bottom
     * horizontal positions: left, right
     * NOTE: horizontal alignments cannot be paired with horizontal positions
     * */
    position: string,
    /**
     * preventOverflow: an object variable; used to prevent the popper from being positioned outside the boundary.
     * options:
     * 'enabled': Boolean type; default set to true, whether the position is preventOverflow enabled or not.
     * 'order': Number type; index used to define the order of execution.
     * 'priority': Array type; ['left','right','top','bottom'], it could overflow on the left and on top of the boundariesElement.
     * 'padding': Number type; amount of pixel used to define a minimum distance between the boundaries.
     * 'boundariesElement': String | HTMLElement type; Boundaries used by Position, can be scrollParent, window, viewport or any DOM element.
     *
     *  More detail in popper.js modifiers: preventOverflow API document.
     * */
    // eslint-disable-next-line react/forbid-prop-types
    preventOverflow: object,
    /**
     * Whether to show the rendered children
     * */
    // eslint-disable-next-line react/boolean-prop-naming
    show: bool,
    /**
     * targetElement: a HTML DOM element
     * The HTML DOM element to be used as the target.
     */
    // eslint-disable-next-line react/forbid-prop-types
    targetElement: object,
    /**
     * Optional style applied to root node.
     */
    // eslint-disable-next-line react/forbid-prop-types
    style: object,
    /**
     * Optional className applied to root node.
     */
    className: string,
    /**
     * Optional id to be passed to the portal
     */
    id: string
  };

  static defaultProps = {
    alignment: 'left',
    offsetX: 0,
    offsetY: 0,
    position: 'top',
    preventOverflow: { boundariesElement: 'offsetParent' },
    show: false,
    targetElement: null,
    className: '',
    componentName: undefined,
    style: null,
    id: undefined
  };

  renderChildrenElement = ({ placement, ref, style }) => {
    const {
      children,
      componentName,
      className,
      style: optionalStyle
    } = this.props;
    const cloneChildren = React.cloneElement(children, {
      placement: placementMap[placement],
      className: `${children.props.className || ''} ${getPlacementClass(
        componentName,
        placement
      )}`.trim(),
      'data-placement': placementMap[placement]
    });

    return (
      <div
        ref={ref}
        style={{ ...optionalStyle, ...style }}
        className={className}
      >
        {cloneChildren}
      </div>
    );
  };

  render() {
    const {
      alignment,
      offsetX,
      offsetY,
      position,
      preventOverflow,
      show,
      targetElement,
      id
    } = this.props;

    const placementKey = getPlacementKey(alignment, position);
    const modifiers = getOffset(offsetX, offsetY);
    modifiers.preventOverflow = preventOverflow;

    // add this 'hide' to avoid the warning in popper
    if (preventOverflow && preventOverflow.enabled === false) {
      modifiers.hide = { enabled: false };
    }

    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      <>
        {show && targetElement && (
          <Portal id={id}>
            <Popper
              referenceElement={targetElement}
              placement={placementKey}
              modifiers={modifiers}
            >
              {this.renderChildrenElement}
            </Popper>
          </Portal>
        )}
      </>
    );
  }
}
